import toast, { type ToastOptions } from 'react-hot-toast';

/**
 * Os quatro níveis de aviso do app.
 *
 * Proposta de 2026-09-12 (mensagens-do-sistema.html). O diagnóstico que gerou
 * isto: **350 `toast.error` contra 160 `toast.success`, e mais nada** — sem um
 * canal para "atenção" e outro para "informação", tudo que não era sucesso
 * saía vermelho. Limite de plano, validação de formulário e queda de rede
 * tinham a mesma cara e o mesmo peso.
 *
 * Em 03/10 as telas passaram a chamar só `aviso.*`. Escolher o nível:
 * - `erro`: a ação foi tentada e falhou (servidor recusou, rede caiu).
 * - `atencao`: nada falhou, mas a pessoa não pode seguir assim — campo
 *   faltando, item repetido, limite do plano, "salve antes de…".
 * - `info`: só contexto, nada a fazer.
 *
 * `opcoes` existe para o `{ id }` que troca um `toast.loading` pelo desfecho.
 */
export const aviso = {
    /** Deu certo. Some sozinho. */
    ok: (msg: string, opcoes?: ToastOptions) => toast.success(msg, opcoes),

    /** Falhou e a pessoa precisa saber. Vermelho, `role="alert"`. */
    erro: (msg: string, opcoes?: ToastOptions) => toast.error(msg, opcoes),

    /**
     * Não falhou, mas tem consequência: limite atingido, algo ficou de fora,
     * o dado salvou mas parte dele foi ignorada. **Este é o nível que faltava**
     * e que estava saindo como erro.
     */
    atencao: (msg: string, opcoes?: ToastOptions) =>
        toast(msg, { ...opcoes, className: 'ds-nivel-atencao' }),

    /** Só contexto. Nada quebrou e nada precisa de ação. */
    info: (msg: string, opcoes?: ToastOptions) =>
        toast(msg, { ...opcoes, className: 'ds-nivel-info' }),

    /** Em andamento. Devolve o id para o desfecho substituir: `aviso.ok(msg, { id })`. */
    carregando: (msg: string) => toast.loading(msg),

    /** Tira um aviso da tela (sem id, tira todos). */
    fechar: (id?: string) => toast.dismiss(id),
};

/** A frase única para "a requisição nem chegou". Eram quatro redações. */
export const SEM_SERVIDOR = 'Não foi possível falar com o servidor.';

/* O que o navegador escreve quando o fetch não sai, em cada motor. */
const FALHA_DE_REDE = /failed to fetch|networkerror|load failed|network request failed/i;

/* Texto escrito para programador: inglês do runtime, JSON, status HTTP. */
const TECNICO = /unexpected token|json|is not a function|is not defined|undefined|null|status code|\bHTTP\b|^\w*Error\b|cannot read|respondeu \d{3}/i;

/**
 * O texto de um erro capturado, pronto para o toast.
 *
 * Os services jogam `new Error(resultado.mensagem)` — a frase que a API
 * escreveu para a pessoa, e essa vale mostrar. O que não vale é o
 * `Erro: Failed to fetch` que aparecia quando a rede caía: aí sai
 * {@link SEM_SERVIDOR}, e o que parece técnico cai no `padrao`.
 */
export function textoDoErro(err: unknown, padrao: string): string {
    const msg = (err instanceof Error ? err.message : typeof err === 'string' ? err : '').trim();
    if (!msg) return padrao;
    if (FALHA_DE_REDE.test(msg)) return SEM_SERVIDOR;
    if (TECNICO.test(msg)) return padrao;
    /* "Erro ao buscar anexos", "Falha ao salvar usuário.": o fallback que os
       services escrevem no `throw` (são ~170) diz o mesmo que o `padrao` da
       tela, só que no formato que a proposta aposentou. */
    if (/^(erro|falha) (ao|na|no)\b/i.test(msg)) return padrao;
    return msg.replace(/^erro:\s*/i, '');
}

export default aviso;
